import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { localCopy } from 'tracked-toolbox';

export interface UiInputSignature {
  Args: {
    disabled?: boolean;
    hasError?: boolean;
    type?: string;
    value?: string | number | null;
    autofocus?: boolean;
    isReadonly?: boolean;
    placeholder?: string;
    id?: string;
    min?: number | string;
    max?: number | string;
    step?: number | string;
    dir?: string;
    update?: (value: string) => void;
    focusIn?: (event: FocusEvent) => void;
    focusOut?: (event: FocusEvent) => void;
  };
  Element: HTMLInputElement;
}

export default class UiInput extends Component<UiInputSignature> {
  @localCopy('args.isReadonly', false) isReadonly!: boolean;
  @localCopy('args.autofocus', false) autofocus!: boolean;
  @localCopy('args.disabled', false) disabled!: boolean;
  @localCopy('args.hasError', false) hasError!: boolean;
  @localCopy('args.type', 'text') type!: string;

  get classes(): string {
    return [this.hasError ? 'error' : '', this.isReadonly ? 'readonly' : ''].join(' ').trim();
  }

  @action
  input(event: Event): void {
    const { value } = event.target as HTMLInputElement;
    this.args.update?.(value);
  }

  @action
  focusIn(event: FocusEvent): void {
    this.args.focusIn?.(event);
  }

  @action
  focusOut(event: FocusEvent): void {
    this.args.focusOut?.(event);
  }

  <template>
    <input
      {{on "input" this.input}}
      {{on "focusin" this.focusIn}}
      {{on "focusout" this.focusOut}}
      class={{this.classes}}
      data-test-error-description={{@hasError}}
      id={{@id}}
      value={{@value}}
      type={{this.type}}
      disabled={{this.disabled}}
      autofocus={{this.autofocus}}
      readonly={{this.isReadonly}}
      placeholder={{@placeholder}}
      min={{@min}}
      max={{@max}}
      step={{@step}}
      dir={{@dir}}
      data-test-ui-input
      ...attributes
    />
  </template>
}
